import type {
  InventoryConnectionDto,
  InventorySnapshotDto,
  InventoryStoreDto,
  InventoryStoreRole,
  InventorySyncRunDto,
} from '../api/inventoryConnection'
import type { PurchaseLineDto, PurchaseOfferDto, PurchasePlanDto } from '../api/inventoryPurchase'
import { isDemoStandEnabled } from './demoStand'
import { inventoryDemoCatalog } from './inventory-demo-catalog'

export async function withPublicInventoryDemo<T>(
  demo: () => T,
  request: () => Promise<T>,
): Promise<T> {
  if (!isDemoStandEnabled()) return request()
  await new Promise((resolve) => setTimeout(resolve, 80))
  return demo()
}

export function demoInventoryConnection(scope: InventoryDemoScope): InventoryConnectionDto {
  return { ...getState(scope).connection }
}

export function saveDemoInventoryConnection(scope: InventoryDemoScope, provider: 'FAKE' | 'IIKO') {
  const state = getState(scope)
  state.connection = { provider, enabled: true, demo: true }
  state.syncedAt = null
  state.runs = []
  return { ...state.connection }
}

export function resetDemoInventory(scope: InventoryDemoScope) {
  states.delete(scopeKey(scope))
  saveDemoInventoryConnection(scope, 'IIKO')
}

export function demoInventoryStores(scope: InventoryDemoScope): InventoryStoreDto[] {
  return getState(scope).stores.map((store) => ({ ...store }))
}

export function updateDemoInventoryStore(
  scope: InventoryDemoScope,
  storeId: number,
  role: InventoryStoreRole,
) {
  const store = getState(scope).stores.find((candidate) => candidate.id === storeId)
  if (!store) throw new Error('Склад демо не найден')
  store.role = role
  return { ...store }
}

export function demoInventorySyncRuns(scope: InventoryDemoScope): InventorySyncRunDto[] {
  return getState(scope).runs.map((run) => ({ ...run }))
}

export function syncDemoInventory(scope: InventoryDemoScope): InventorySnapshotDto {
  const state = getState(scope)
  state.syncedAt = new Date().toISOString()
  state.runs = [
    {
      id: state.nextRunId++,
      accounting_date: accountingDate(),
      started_at: state.syncedAt,
      finished_at: state.syncedAt,
      status: 'OK' as const,
      error: null,
    },
    ...state.runs,
  ].slice(0, 10)
  return demoInventorySnapshot(scope)
}

export function demoInventorySnapshot(scope: InventoryDemoScope): InventorySnapshotDto {
  const state = getState(scope)
  return {
    provider: state.connection.provider,
    demo: true,
    accounting_date: accountingDate(),
    synced_at: state.syncedAt,
    error: null,
    groups: demoPurchasePlan(scope).lines.map((line) => ({ ...line })),
  }
}

export function demoPurchasePlan(
  scope: InventoryDemoScope,
  usageWindowDays: number | null = null,
): PurchasePlanDto {
  if (usageWindowDays != null && !horizons.includes(usageWindowDays))
    throw new Error('Неизвестное окно расхода')
  const state = getState(scope)
  const orderDate = moscowDate()
  const snapshotDate = accountingDate()
  const lines: PurchaseLineDto[] = []
  if (state.syncedAt) {
    for (const product of products) {
      const store = state.stores.find((candidate) => candidate.external_id === product.store)
      if (!store || store.role === 'IGNORE') continue
      const balances = productBalances(product, snapshotDate)
      const onHand = balances.at(-1)?.quantity ?? 0
      const windows = horizons.map((days) => {
        const drops = balances.slice(1).flatMap((point, index) => {
          const drop = round(balances[index]!.quantity - point.quantity)
          return point.date >= addDays(snapshotDate, 1 - days) && drop > 0 ? [drop] : []
        })
        return {
          days,
          daily_usage: drops.length >= 7 ? median(drops) : null,
          drop_days: drops.length,
        }
      })
      const longest = [...windows].reverse().find((window) => window.daily_usage != null)
      const week = windows[0]!
      const shifted =
        usageWindowDays == null &&
        longest?.daily_usage != null &&
        week.daily_usage != null &&
        Math.max(longest.daily_usage, week.daily_usage) >
          Math.min(longest.daily_usage, week.daily_usage) * 1.5
      const chosen =
        usageWindowDays == null
          ? shifted
            ? week
            : longest
          : windows.find((window) => window.days === usageWindowDays && window.daily_usage != null)
      const dailyUsage = chosen?.daily_usage ?? null
      const offers = product.offers
        .map((offer) => buildOffer(offer, dailyUsage, onHand, orderDate))
        .sort((a, b) => a.price / a.base_quantity_per_unit! - b.price / b.base_quantity_per_unit!)
        .map((offer, index) => ({ ...offer, recommended: index === 0 }))
      lines.push({
        store_external_id: product.store,
        store_role: store.role,
        product_external_id: product.id,
        product_name: product.name,
        unit: product.unit,
        on_hand: onHand,
        windows,
        usage_window_days: chosen?.days ?? null,
        daily_usage: dailyUsage,
        usage_shifted: shifted,
        insufficient_history: dailyUsage == null,
        order_quantity: dailyUsage == null ? null : requiredQuantity(dailyUsage, onHand, 4),
        offers,
      })
    }
  }
  return {
    order_date: orderDate,
    snapshot_date: state.syncedAt ? snapshotDate : null,
    required_snapshot_date: snapshotDate,
    snapshot_synced_at: state.syncedAt,
    stale: !state.syncedAt,
    cover_days: 1,
    stock_days: 3,
    delivery_same_day: false,
    delivery_next_day: true,
    delivery_weekends: true,
    lines,
  }
}

function buildOffer(
  definition: DemoOfferDefinition,
  usage: number | null,
  onHand: number,
  orderDate: string,
): PurchaseOfferDto {
  const source = inventoryDemoCatalog.find((item) => item.id === definition.id)
  if (!source) throw new Error('Предложение отсутствует в прайсе демо')
  let deliveryDate = addDays(orderDate, definition.sameDay ? 0 : 1)
  while (!definition.weekends && [0, 6].includes(dayOfWeek(deliveryDate)))
    deliveryDate = addDays(deliveryDate, 1)
  const coverDays = Math.max(
    1,
    Math.round((Date.parse(deliveryDate) - Date.parse(orderDate)) / 86_400_000),
  )
  const required = usage == null ? null : requiredQuantity(usage, onHand, coverDays + 3)
  const quantity =
    required == null
      ? null
      : round(Math.ceil(required / definition.base / definition.step - 1e-9) * definition.step)
  return {
    catalog_item_id: source.id,
    product_type_id: source.productTypeId,
    private_supplier_id: source.supplierId,
    supplier_name: source.supplierName,
    item_name: source.name,
    price: source.price,
    unit: source.unit,
    package_size: source.packageSize,
    quantity,
    base_quantity_per_unit: definition.base,
    line_total: quantity == null ? null : Math.round(quantity * source.price * 100) / 100,
    recommended: false,
    delivery_date: deliveryDate,
    cover_days: coverDays,
    order_quantity: quantity == null ? null : round(quantity * definition.base),
  }
}

function productBalances(product: DemoProduct, end: string): DemoBalance[] {
  const length = product.newProduct ? 5 : 91
  let quantity = round(product.servings * product.perServing * product.targetDays)
  const balances: DemoBalance[] = []
  for (let index = 0; index < length; index++) {
    const date = addDays(end, index - length + 1)
    const day = dayOfWeek(date)
    const factor = guestFactors[day]!
    const isRecent = index >= length - 7
    const demand = round(
      Math.round(product.servings * factor * (isRecent && product.increasing ? 1.8 : 1)) *
        product.perServing,
    )
    // Приходы по понедельникам и четвергам; хозяйственный остаток не входит в расчёт.
    if (index > 0 && (product.targetDays >= 10 ? (index + 4) % 14 === 0 : [1, 4].includes(day))) {
      const target = round(product.servings * product.perServing * product.targetDays)
      quantity = Math.max(quantity, target)
    }
    if (index > 0) quantity = round(Math.max(0, quantity - demand))
    balances.push({ date, quantity })
  }
  return balances
}

function getState(scope: InventoryDemoScope): DemoInventoryState {
  const key = scopeKey(scope)
  let state = states.get(key)
  if (state) return state
  const date = accountingDate()
  const syncedAt = `${date}T20:10:00.000Z`
  state = {
    connection: { provider: 'IIKO', enabled: true, demo: true },
    syncedAt,
    nextRunId: 2,
    stores: [
      { id: 1, external_id: 'demo-kitchen', name: 'Основной склад', role: 'KITCHEN' },
      { id: 2, external_id: 'demo-bar', name: 'Бар', role: 'BAR' },
      { id: 3, external_id: 'demo-supplies', name: 'Хозтовары и упаковка', role: 'IGNORE' },
    ],
    runs: [
      {
        id: 1,
        accounting_date: date,
        started_at: syncedAt,
        finished_at: syncedAt,
        status: 'OK',
        error: null,
      },
    ],
  }
  states.set(key, state)
  return state
}

function scopeKey({ organizationId, venueId }: InventoryDemoScope) {
  return `${organizationId}:${venueId}`
}
function moscowDate(now = new Date()) {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Moscow' }).format(now)
}
function accountingDate(now = new Date()) {
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Moscow',
      hour: '2-digit',
      hourCycle: 'h23',
    }).format(now),
  )
  const minute = Number(
    new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Moscow', minute: '2-digit' }).format(now),
  )
  return addDays(moscowDate(now), hour > 23 || (hour === 23 && minute >= 10) ? 0 : -1)
}
function addDays(date: string, days: number) {
  return new Date(Date.parse(`${date}T12:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10)
}
function dayOfWeek(date: string) {
  return new Date(`${date}T12:00:00Z`).getUTCDay()
}
function round(value: number) {
  return Math.round(value * 10000) / 10000
}
function median(values: number[]) {
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  return round(sorted.length % 2 ? sorted[middle]! : (sorted[middle - 1]! + sorted[middle]!) / 2)
}
function requiredQuantity(usage: number, stock: number, days: number) {
  return round(Math.max(0, usage * days - stock))
}

const states = new Map<string, DemoInventoryState>()
const horizons = [7, 14, 21, 30, 60, 90]
// 50 мест; относительная загрузка от 60 гостей в понедельник до 110 в субботу.
const guestFactors = [1, 0.75, 0.85, 1, 1, 1.25, 1.375]
const nextDay = { sameDay: false, weekends: true }
const products: DemoProduct[] = [
  {
    id: 'chicken',
    name: 'Филе куриное',
    unit: 'кг',
    store: 'demo-kitchen',
    servings: 20,
    perServing: 0.15,
    targetDays: 4,
    offers: [
      { id: 14193, base: 1, step: 1, ...nextDay },
      { id: 14194, base: 1, step: 1, ...nextDay },
    ],
  },
  {
    id: 'beef',
    name: 'Фарш говяжий',
    unit: 'кг',
    store: 'demo-kitchen',
    servings: 18,
    perServing: 0.16,
    targetDays: 4,
    offers: [
      { id: 15748, base: 1, step: 3.6, ...nextDay },
      { id: 15749, base: 1, step: 3.6, ...nextDay },
    ],
  },
  {
    id: 'fries',
    name: 'Картофель фри',
    unit: 'кг',
    store: 'demo-kitchen',
    servings: 35,
    perServing: 0.15,
    targetDays: 5,
    offers: [
      { id: 16430, base: 2.5, step: 1, ...nextDay },
      { id: 16442, base: 1, step: 2.5, ...nextDay },
    ],
  },
  {
    id: 'pasta',
    name: 'Паста сухая',
    unit: 'кг',
    store: 'demo-kitchen',
    servings: 16,
    perServing: 0.1,
    targetDays: 16,
    offers: [{ id: 14954, base: 0.5, step: 1, ...nextDay }],
  },
  {
    id: 'cream',
    name: 'Сливки 22%',
    unit: 'л',
    store: 'demo-kitchen',
    servings: 16,
    perServing: 0.05,
    targetDays: 4,
    offers: [{ id: 14891, base: 1, step: 1, ...nextDay }],
  },
  {
    id: 'mozzarella',
    name: 'Моцарелла для горячих блюд',
    unit: 'кг',
    store: 'demo-kitchen',
    servings: 12,
    perServing: 0.12,
    targetDays: 4,
    offers: [
      { id: 13715, base: 1, step: 2, ...nextDay },
      { id: 14262, base: 1, step: 1, sameDay: false, weekends: false },
    ],
  },
  {
    id: 'oil',
    name: 'Масло подсолнечное',
    unit: 'л',
    store: 'demo-kitchen',
    servings: 40,
    perServing: 0.02,
    targetDays: 16,
    offers: [{ id: 16325, base: 5, step: 1, ...nextDay }],
  },
  {
    id: 'pickles',
    name: 'Огурцы маринованные для бургеров',
    unit: 'кг',
    store: 'demo-kitchen',
    servings: 18,
    perServing: 0.03,
    targetDays: 16,
    offers: [{ id: 16395, base: 1, step: 5, ...nextDay }],
  },
  {
    id: 'milk',
    name: 'Молоко 3,2%',
    unit: 'л',
    store: 'demo-bar',
    servings: 32,
    perServing: 0.16,
    targetDays: 4,
    offers: [
      { id: 17690, base: 1, step: 1, ...nextDay },
      { id: 17692, base: 1, step: 1, ...nextDay },
    ],
  },
  {
    id: 'coffee',
    name: 'Кофе зерновой',
    unit: 'кг',
    store: 'demo-bar',
    servings: 40,
    perServing: 0.018,
    targetDays: 16,
    offers: [{ id: 16639, base: 1, step: 1, ...nextDay }],
  },
  {
    id: 'lime',
    name: 'Лайм',
    unit: 'кг',
    store: 'demo-bar',
    servings: 12,
    perServing: 0.06,
    targetDays: 4,
    offers: [
      { id: 15540, base: 1, step: 0.5, ...nextDay },
      { id: 14226, base: 1, step: 0.5, sameDay: true, weekends: false },
    ],
  },
  {
    id: 'mint',
    name: 'Мята свежая',
    unit: 'кг',
    store: 'demo-bar',
    servings: 12,
    perServing: 0.005,
    targetDays: 3,
    offers: [
      { id: 15338, base: 1, step: 0.05, sameDay: true, weekends: true },
      { id: 15489, base: 1, step: 0.05, ...nextDay },
    ],
  },
  {
    id: 'coconut',
    name: 'Кокосовый напиток для кофе',
    unit: 'л',
    store: 'demo-bar',
    servings: 4,
    perServing: 0.15,
    targetDays: 16,
    increasing: true,
    offers: [{ id: 17323, base: 1, step: 1, ...nextDay }],
  },
  {
    id: 'passion-fruit',
    name: 'Пюре маракуйя — новое меню',
    unit: 'кг',
    store: 'demo-bar',
    servings: 10,
    perServing: 0.03,
    targetDays: 4,
    newProduct: true,
    offers: [{ id: 17857, base: 1, step: 1, ...nextDay }],
  },
]

export interface InventoryDemoScope {
  organizationId: number
  venueId: number
}
interface DemoInventoryState {
  connection: InventoryConnectionDto
  syncedAt: string | null
  stores: InventoryStoreDto[]
  runs: InventorySyncRunDto[]
  nextRunId: number
}
interface DemoOfferDefinition {
  id: number
  base: number
  step: number
  sameDay: boolean
  weekends: boolean
}
interface DemoProduct {
  id: string
  name: string
  unit: string
  store: string
  servings: number
  perServing: number
  targetDays: number
  offers: DemoOfferDefinition[]
  increasing?: boolean
  newProduct?: boolean
}
interface DemoBalance {
  date: string
  quantity: number
}
